import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import { 
    Search, ChevronDown, ChevronUp, MessageCircle, 
    Phone, Mail, HelpCircle, Star, Shield, Calendar,
    CreditCard, Download, User, Zap, Globe, Filter,
    BookOpen, Users, Settings, Smartphone, ArrowRight,
    Lightbulb, CheckCircle, Clock, Globe2
} from 'lucide-react';

const faqs = [
    {
        id: 1,
        category: "Platform",
        question: "What is EventPlots?",
        answer: "EventPlots is Uganda's leading digital ticketing platform that revolutionizes how events are created, managed, and attended. We connect event organizers with audiences through a seamless, secure, and user-friendly experience.",
        icon: <Globe className="w-5 h-5" />
    },
    {
        id: 2,
        category: "Account",
        question: "How do I create an account?",
        answer: "Getting started is easy! Simply click 'Sign Up' on our homepage. You can register with your email, phone number, or use social login (Facebook/Google) for instant access to all features.",
        icon: <User className="w-5 h-5" />
    },
    {
        id: 3,
        category: "Events",
        question: "What types of events are available?",
        answer: "We host everything from music concerts and sports events to business conferences, cultural festivals, workshops, comedy shows, theater performances, and much more. There's truly something for everyone!",
        icon: <Calendar className="w-5 h-5" />
    },
    {
        id: 4,
        category: "Security",
        question: "How secure is my information?",
        answer: "Your security is our top priority. We use enterprise-grade encryption, secure payment processing, and strict data protection protocols. Your personal and payment information is never shared without your explicit consent.",
        icon: <Shield className="w-5 h-5" />
    },
    {
        id: 5,
        category: "Payment",
        question: "What payment methods do you accept?",
        answer: "We offer multiple secure payment options: Visa, MasterCard, MTN Mobile Money, Airtel Money, and bank transfers. Available methods may vary by event and location for your convenience.",
        icon: <CreditCard className="w-5 h-5" />
    },
    {
        id: 6,
        category: "Tickets",
        question: "How do I access my tickets?",
        answer: "After purchase, you'll receive an email and SMS with your ticket download link. You can also access all your tickets anytime by logging into your account and visiting the 'My Tickets' section.",
        icon: <Download className="w-5 h-5" />
    },
    {
        id: 7,
        category: "Support",
        question: "How can I get help?",
        answer: "Our dedicated support team is available 24/7! Contact us via email at support@eventplots.com, call +256 200 905 932, or use our live chat feature. We're here to help with any questions or concerns.",
        icon: <MessageCircle className="w-5 h-5" />
    },
    {
        id: 8,
        category: "Events",
        question: "What happens if an event is cancelled?",
        answer: "If an event is cancelled, we'll notify you immediately via email and SMS. Full refunds are processed within 5-7 business days. For postponements, your ticket remains valid for the new date.",
        icon: <Calendar className="w-5 h-5" />
    },
    {
        id: 9,
        category: "Payment",
        question: "Are there service fees?",
        answer: "We charge a small service fee per ticket to maintain our platform and provide excellent customer support. The fee varies by event type and payment method, ensuring transparent pricing.",
        icon: <CreditCard className="w-5 h-5" />
    },
    {
        id: 10,
        category: "Tickets",
        question: "Can I transfer my ticket?",
        answer: "Ticket transfer policies vary by event and are set by the organizer. Check the event details page for specific transfer policies or contact the event organizer directly for assistance.",
        icon: <Download className="w-5 h-5" />
    },
    {
        id: 11,
        category: "Events",
        question: "Can I create free events?",
        answer: "Absolutely! You can create free events on EventPlots. While tickets are free, there may be minimal processing fees for payment methods used to cover transaction costs.",
        icon: <Calendar className="w-5 h-5" />
    },
    {
        id: 12,
        category: "Support",
        question: "I'm having technical issues",
        answer: "Don't worry! Our technical support team is here to help. Contact us immediately and we'll resolve any payment issues, technical problems, or other concerns you might have.",
        icon: <MessageCircle className="w-5 h-5" />
    }
];

const categories = [
    { name: "All", icon: <BookOpen className="w-4 h-4" />, count: faqs.length },
    { name: "Platform", icon: <Globe className="w-4 h-4" />, count: faqs.filter(f => f.category === "Platform").length },
    { name: "Account", icon: <User className="w-4 h-4" />, count: faqs.filter(f => f.category === "Account").length },
    { name: "Events", icon: <Calendar className="w-4 h-4" />, count: faqs.filter(f => f.category === "Events").length },
    { name: "Payment", icon: <CreditCard className="w-4 h-4" />, count: faqs.filter(f => f.category === "Payment").length },
    { name: "Tickets", icon: <Download className="w-4 h-4" />, count: faqs.filter(f => f.category === "Tickets").length },
    { name: "Support", icon: <MessageCircle className="w-4 h-4" />, count: faqs.filter(f => f.category === "Support").length },
    { name: "Security", icon: <Shield className="w-4 h-4" />, count: faqs.filter(f => f.category === "Security").length }
];

const Faq = () => {
    const [openIndex, setOpenIndex] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const filteredFaqs = faqs.filter(faq => {
        const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50">
                {/* Hero Section */}
                <div className="bg-white border-b border-gray-200">
                    <div className="container mx-auto px-4 py-16">
                        <div className="max-w-3xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="mb-8"
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-2xl mb-6">
                                    <HelpCircle className="w-8 h-8 text-orange-600" />
                                </div>
                                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                                    Frequently Asked Questions
                                </h1>
                                <p className="text-lg text-gray-600">
                                    Find quick answers to common questions about EventPlots
                                </p>
                            </motion.div>

                            {/* Search Bar */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2, duration: 0.6 }}
                                className="relative max-w-lg mx-auto"
                            >
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search questions..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 shadow-sm transition-all duration-200"
                                />
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* Category Filter */}
                <div className="bg-white border-b border-gray-200">
                    <div className="container mx-auto px-4 py-8">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Browse by Category</h2>
                                <p className="text-gray-600">Select a category to filter questions or view all</p>
                            </div>
                            <div className="flex flex-wrap gap-4 justify-center">
                                {categories.map((category) => (
                                    <motion.button
                                        key={category.name}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => setSelectedCategory(category.name)}
                                        className={`group relative flex items-center gap-3 px-6 py-4 rounded-2xl font-semibold transition-all duration-300 ${
                                            selectedCategory === category.name
                                                ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-lg shadow-orange-200 transform scale-105'
                                                : 'bg-white text-gray-700 hover:bg-orange-50 hover:text-orange-700 border-2 border-gray-200 hover:border-orange-300 hover:shadow-md'
                                        }`}
                                    >
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                                            selectedCategory === category.name
                                                ? 'bg-white bg-opacity-20'
                                                : 'bg-orange-100 group-hover:bg-orange-200'
                                        }`}>
                                            <div className={`${
                                                selectedCategory === category.name
                                                    ? 'text-white'
                                                    : 'text-orange-600 group-hover:text-orange-700'
                                            }`}>
                                                {category.icon}
                                            </div>
                                        </div>
                                        <div className="text-left">
                                            <div className="font-bold text-lg">{category.name}</div>
                                            <div className={`text-sm ${
                                                selectedCategory === category.name
                                                    ? 'text-white text-opacity-90'
                                                    : 'text-gray-500 group-hover:text-orange-600'
                                            }`}>
                                                {category.count} question{category.count !== 1 ? 's' : ''}
                                            </div>
                                        </div>
                                        {selectedCategory === category.name && (
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                className="absolute -top-2 -right-2 w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center"
                                            >
                                                <CheckCircle className="w-4 h-4 text-white" />
                                            </motion.div>
                                        )}
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 py-12">
                    <div className="max-w-6xl">
                        {/* Results Summary */}
                        {(searchTerm || selectedCategory !== 'All') && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mb-8"
                            >
                                <p className="text-gray-600">
                                    {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} found
                                    {searchTerm && ` for "${searchTerm}"`}
                                </p>
                            </motion.div>
                        )}

                        {/* FAQ List */}
                        <div className="grid gap-6 w-full">
                            <AnimatePresence>
                                {filteredFaqs.map((faq, index) => (
                                    <motion.div
                                        key={faq.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ delay: index * 0.05, duration: 0.3 }}
                                        className="group bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg hover:border-orange-200 transition-all duration-300 w-full"
                                    >
                                        <div id='button'
                                            className="w-full p-8 text-left hover:bg-gradient-to-r hover:from-orange-50 hover:to-white transition-all duration-300"
                                            onClick={() => toggleFAQ(index)}
                                        >
                                            <div className="flex items-start gap-6">
                                                <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                                                    <div className="text-orange-600">
                                                        {faq.icon}
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-bold text-gray-900 text-xl mb-3 leading-tight group-hover:text-orange-700 transition-colors duration-300">
                                                        {faq.question}
                                                    </h3>
                                                    <div className="flex items-center gap-4">
                                                        <span className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-orange-200 text-orange-700 px-4 py-2 rounded-full font-medium text-sm border border-orange-200">
                                                            {faq.icon}
                                                            {faq.category}
                                                        </span>
                                                        <div className="flex items-center gap-2 text-gray-500">
                                                            <span className="text-sm font-medium">Click to {openIndex === index ? 'hide' : 'view'} answer</span>
                                                            {openIndex === index ? (
                                                                <ChevronUp className="w-5 h-5 text-orange-500" />
                                                            ) : (
                                                                <ChevronDown className="w-5 h-5 text-orange-500" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <AnimatePresence>
                                            {openIndex === index && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.4, ease: "easeInOut" }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-8 pb-8">
                                                        <div className="border-t border-gradient-to-r from-orange-200 to-gray-200 pt-6">
                                                            <div className="bg-gradient-to-r from-orange-50 to-white rounded-xl p-6 border border-orange-100">
                                                                <p className="text-gray-700 leading-relaxed text-lg">
                                                                    {faq.answer}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        {/* No Results */}
                        {filteredFaqs.length === 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-16"
                            >
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Search className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                                    No answers found
                                </h3>
                                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                                    Try different keywords or browse all categories to find what you're looking for.
                                </p>
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedCategory('All');
                                    }}
                                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200"
                                >
                                    Clear Search
                                </button>
                            </motion.div>
                        )}

                        {/* Contact Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                            className="mt-16 bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm"
                        >
                            <div className="max-w-2xl mx-auto">
                                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                    <MessageCircle className="w-8 h-8 text-orange-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                    Still need help?
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    Our support team is ready to assist you with any questions or concerns.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <a
                                        href="mailto:support@eventplots.com"
                                        className="flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                                    >
                                        <Mail className="w-5 h-5" />
                                        Email Support
                                    </a>
                                    <a
                                        href="tel:+256200905932"
                                        className="flex items-center justify-center gap-3 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                                    >
                                        <Phone className="w-5 h-5" />
                                        Call Us
                                    </a>
                                    <a
                                        href="/contact"
                                        className="flex items-center justify-center gap-3 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                                    >
                                        <MessageCircle className="w-5 h-5" />
                                        Contact Form
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Faq;
